import { ComponentProps, FC, useMemo } from 'react';

import DatePicker from 'react-datepicker';
import { CgClose } from 'react-icons/cg';
import { FaRegCalendar } from 'react-icons/fa';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import Input from '../Input/Input';

type PROPS = ComponentProps<typeof DatePicker> & {
  className?: string | string[];
  size?: SIZE | 'default';
  textType?: 'default' | 'danger' | 'success' | 'warning';
  required?: boolean;
  label?: string;
  labelSubText?: string;
  subText?: string;
  name: string;
  information?: string;
  onChange: (
    name: string,
    date: Date | [Date | null, Date | null] | null,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  selected?: Date | null;
  clearSelected?: () => void;
  showTimeSelect?: boolean;
  timeIntervals?: number;
  timeCaption?: string;
};

const DateField: FC<PROPS> = ({
  className,
  size = SIZE.Small,
  textType,
  required,
  label,
  labelSubText = '(Optional)',
  subText,
  name,
  information,
  onChange,
  placeholder,
  disabled,
  selected,
  value,
  clearSelected,
  dateFormat = 'MMMM d, yyyy | h:mm aa', // Updated default format
  showTimeSelect = false,
  timeIntervals = 15,
  timeCaption = 'Time',
  ...props
}) => {
  const addon: ComponentProps<typeof Input>['addon'] = {
    icon: selected ? CgClose : FaRegCalendar,
    ...(selected && { onClick: clearSelected }),
  };

  const defaultDateFormat = useMemo(() => {
    if (dateFormat === 'MMMM d, yyyy | h:mm aa' && !showTimeSelect) {
      return 'MMMM d, yyyy';
    }

    return dateFormat;
  }, [dateFormat, showTimeSelect]);

  return (
    <div
      className={cn(
        'relative',
        'datefield w-full',
        disabled && 'disabled',
        size,
        textType,
        className,
      )}
    >
      <div className="datepicker-container w-full">
        <DatePicker
          popperProps={{ strategy: 'fixed' }}
          selected={selected}
          dateFormat={defaultDateFormat}
          showTimeSelect={showTimeSelect}
          timeIntervals={timeIntervals}
          timeCaption={timeCaption}
          customInput={
            <Input
              label={label}
              required={required}
              information={information}
              labelSubText={labelSubText}
              size={size}
              subText={subText}
              textType={textType}
              value={value}
              addon={addon}
            />
          }
          required={required}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div
              className={cn(
                'pb-2 mx-2',
                'flex items-center justify-between',
                'border-b border-separator-primary',
              )}
            >
              <p className="text-sm font-semibold text-secondary-dark">
                {date.toLocaleString('default', { month: 'long' })}{' '}
                {date.getFullYear()}
              </p>
              <div className="flex items-center gap-x-5">
                <button
                  type="button"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  className={cn(
                    'text-content',
                    'transition',
                    'hover:text-primary-base disabled:bg-transparent disabled:text-disabled',
                  )}
                >
                  <MdNavigateBefore className="text-lg" />
                </button>
                <button
                  type="button"
                  className={cn(
                    'text-content',
                    'transition',
                    'hover:text-primary-base disabled:bg-transparent disabled:text-disabled',
                  )}
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                >
                  <MdNavigateNext className="text-lg" />
                </button>
              </div>
            </div>
          )}
          placeholderText={placeholder}
          disabled={disabled}
          onChange={(date) => onChange(name, date)}
          formatWeekDay={(nameOfWeek) => nameOfWeek[0]}
          className="focus:border-separator-primary w-full"
          showYearDropdown
          {...props}
        />
      </div>
    </div>
  );
};

export default DateField;
